if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        this.form.querySelector("[name=id]").disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === "true";
        this.useOrderPage = true;
      }

      async goToOrderPage() {
        if (typeof window.refreshHeaderCartCount === "function") {
          await window.refreshHeaderCartCount();
        }
        window.location.href = "/cart";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.handleErrorMessage();
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading-overlay__spinner")?.classList.remove(
          "hidden",
        );
        this.submitButton
          .querySelector("[data-addtocart-text]")
          .classList.add("hidden");

        const formData = new FormData(this.form);
        const variantId = formData.get("id");
        const requestedQty = parseInt(formData.get("quantity"), 10) || 1;

        // Shopify's /cart/add has a limit of ~20 items per request.
        // To work around this, first check the cart for existing items of
        // this variant, then use /cart/update to set the total quantity directly.
        fetch(`${window.routes?.cart_url || "/cart"}.js`)
          .then((res) => res.json())
          .then((cart) => {
            const existingQty = (cart.items || [])
              .filter((item) => String(item.variant_id) === String(variantId))
              .reduce((sum, item) => sum + (item.quantity || 0), 0);
            const newTotalQty = existingQty + requestedQty;

            if (existingQty > 0) {
              // Variant already in cart — use /cart/update to set total quantity
              // Check quantity_rule.max and cap if needed
              const qtyInput = document.querySelector(
                "input[data-input-quantity]",
              );
              const maxRule = qtyInput
                ? parseInt(qtyInput.getAttribute("data-max-rule"), 10)
                : NaN;
              let cappedTotalQty = newTotalQty;
              let wasCapped = false;
              if (!isNaN(maxRule) && maxRule > 0 && newTotalQty > maxRule) {
                cappedTotalQty = maxRule;
                wasCapped = true;
              }

              // If capped and already at max, skip the update entirely — just show error
              if (wasCapped && existingQty >= cappedTotalQty) {
                return {
                  updateResponse: null,
                  isUpdate: true,
                  wasCapped,
                  maxRule,
                  cappedTotalQty,
                };
              }

              const body = JSON.stringify({
                updates: { [variantId]: cappedTotalQty },
              });
              return fetch(`${routes.cart_update_url}`, {
                ...fetchConfig(),
                body,
              })
                .then((r) => r.json())
                .then((updateResponse) => {
                  return {
                    updateResponse,
                    isUpdate: true,
                    wasCapped,
                    maxRule,
                    cappedTotalQty,
                  };
                });
            } else {
              // Variant not in cart — use normal /cart/add
              const config = fetchConfig("javascript");
              config.headers["X-Requested-With"] = "XMLHttpRequest";
              delete config.headers["Content-Type"];
              config.body = formData;
              return fetch(`${routes.cart_add_url}`, config)
                .then((r) => r.json())
                .then((addResponse) => ({ addResponse, isUpdate: false }));
            }
          })
          .then((result) => {
            if (result.isUpdate) {
              // Handled via /cart/update
              if (result.wasCapped) {
                const errorMsg = this.dataset.quantityError || "";
                this.handleErrorMessage(errorMsg);
                this.error = true;
                // Ensure drawer stays closed
                if (this.cart) {
                  this.cart.classList.remove("active");
                  document.querySelector("body").classList.remove("no-scroll");
                }
                // Sync quantity label with actual cart state
                const cartItemsEl = document.querySelector("cart-items");
                if (
                  cartItemsEl &&
                  typeof cartItemsEl.syncProductCartQuantityLabel === "function"
                ) {
                  cartItemsEl.syncProductCartQuantityLabel();
                }
                return;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: variantId,
              });
              this.error = false;
              if (this.dataset.addToCartOnly === "true") {
                return;
              }
              this.goToOrderPage();
            } else {
              // Handled via /cart/add
              const response = result.addResponse;
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: "product-form",
                  productVariantId: variantId,
                  errors: response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);
                this.submitButton.setAttribute("aria-disabled", true);
                this.error = true;
                const cartItemsEl = document.querySelector("cart-items");
                if (
                  cartItemsEl &&
                  typeof cartItemsEl.syncProductCartQuantityLabel === "function"
                ) {
                  cartItemsEl.syncProductCartQuantityLabel();
                }
                return;
              }
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: variantId,
              });
              this.error = false;
              if (this.dataset.addToCartOnly === "true") {
                return;
              }
              this.goToOrderPage();
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            this.submitButton
              .querySelector("[data-addtocart-text]")
              .classList.remove("hidden");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading-overlay__spinner")?.classList.add(
              "hidden",
            );
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );
        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);
        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
